import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Plus, Users, Share2, Moon, CalendarDays, Pencil, LogOut, Trash2, X, Check, UserMinus, Crown, Copy } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { groupsAPI } from '../utils/api';
import BottomNav from '../components/BottomNav';

const GroupsPage = () => {
  const navigate = useNavigate();
  const { groupId } = useParams();
  const { user } = useAuth();
  const { t } = useI18n();

  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [loading, setLoading] = useState(true);

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [creating, setCreating] = useState(false);

  // Invite modal
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);

  // Join by code modal
  const [showJoinCodeModal, setShowJoinCodeModal] = useState(false);
  const [joinCodeInput, setJoinCodeInput] = useState('');
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState('');

  // Rename
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);

  // Remove member confirm
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [removing, setRemoving] = useState(false);

  // Leave confirm
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [leaving, setLeaving] = useState(false);

  // Delete confirm
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { loadGroups(); }, []);

  useEffect(() => {
    if (groupId && groups.length > 0) loadGroupDetail(groupId);
    else if (!groupId) setSelectedGroup(null);
  }, [groupId, groups]);

  const loadGroups = async () => {
    try {
      const grps = await groupsAPI.getAll();
      setGroups(Array.isArray(grps) ? grps : []);
    } catch (err) {
      console.error('Failed to load groups:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadGroupDetail = async (id) => {
    try {
      const group = await groupsAPI.get(id);
      setSelectedGroup(group);
    } catch (err) {
      console.error('Failed to load group:', err);
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await groupsAPI.create({ name: newGroupName });
      setNewGroupName('');
      setShowCreateModal(false);
      await loadGroups();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === 'GROUP_LIMIT_REACHED') {
        alert(t('limits.group_full'));
      } else {
        console.error('Failed to create group:', err);
      }
    } finally {
      setCreating(false);
    }
  };

  const handleRename = async () => {
    if (!renameValue.trim() || renameValue.trim() === selectedGroup.name) {
      setIsRenaming(false);
      return;
    }
    setRenaming(true);
    try {
      await groupsAPI.rename(selectedGroup.id, renameValue.trim());
      setSelectedGroup(prev => ({ ...prev, name: renameValue.trim() }));
      setIsRenaming(false);
    } catch (err) {
      console.error('Failed to rename group:', err);
    } finally {
      setRenaming(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!memberToRemove) return;
    setRemoving(true);
    try {
      await groupsAPI.removeMember(selectedGroup.id, memberToRemove.id);
      setSelectedGroup(prev => ({
        ...prev,
        memberDetails: prev.memberDetails.filter(m => m.id !== memberToRemove.id),
        members: prev.members?.filter(id => id !== memberToRemove.id),
      }));
      setMemberToRemove(null);
    } catch (err) {
      console.error('Failed to remove member:', err);
    } finally {
      setRemoving(false);
    }
  };

  const handleLeave = async () => {
    setLeaving(true);
    try {
      await groupsAPI.leave(selectedGroup.id);
      navigate('/groups');
      await loadGroups();
    } catch (err) {
      console.error('Failed to leave group:', err);
    } finally {
      setLeaving(false);
      setShowLeaveConfirm(false);
    }
  };

  const handleDeleteGroup = async () => {
    setDeleting(true);
    try {
      await groupsAPI.delete(selectedGroup.id);
      navigate('/groups');
      await loadGroups();
    } catch (err) {
      console.error('Failed to delete group:', err);
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const generateInviteLink = (group) => {
    // Use inviteToken in the URL — safer than exposing the MongoDB ObjectId
    const token = group.inviteToken || group.id;
    setInviteLink(`${window.location.origin}/invite/${token}`);
    setInviteCode(group.groupCode || '');
    setShowInviteModal(true);
  };

  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const copyInviteCode = () => {
    navigator.clipboard.writeText(inviteCode);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const handleJoinByCode = async (e) => {
    e.preventDefault();
    const code = joinCodeInput.trim().toUpperCase();
    if (!code) return;
    setJoining(true);
    setJoinError('');
    try {
      const res = await groupsAPI.joinByCode(code);
      setShowJoinCodeModal(false);
      setJoinCodeInput('');
      await loadGroups();
      if (res.id) navigate(`/groups/${res.id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setJoinError(typeof detail === 'string' ? detail : t('invite.error_failed'));
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full max-w-md mx-auto min-h-screen bg-[#F7F9FB] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full" />
      </div>
    );
  }

  // ── GROUP DETAIL VIEW ────────────────────────────────────────
  if (selectedGroup) {
    const isAdmin = selectedGroup.createdBy === user?.id;

    return (
      <div className="w-full max-w-md mx-auto min-h-screen bg-[#F7F9FB] relative flex flex-col">
        <div className="p-6 pb-28 flex-1 overflow-y-auto">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>

            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
              <button onClick={() => navigate('/groups')} data-testid="back-to-groups"
                className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm active:scale-95 transition-transform shrink-0">
                <ArrowLeft size={20} className="text-slate-900" />
              </button>
              {isRenaming ? (
                <div className="flex items-center gap-2 flex-1">
                  <input
                    autoFocus
                    data-testid="rename-group-input"
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setIsRenaming(false); }}
                    className="flex-1 text-2xl font-bold text-slate-900 bg-white border border-slate-200 rounded-xl px-3 py-1 outline-none"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                  />
                  <button onClick={handleRename} disabled={renaming} data-testid="rename-save-btn"
                    className="w-9 h-9 bg-slate-900 text-white rounded-full flex items-center justify-center active:scale-95">
                    <Check size={16} />
                  </button>
                  <button onClick={() => setIsRenaming(false)} className="w-9 h-9 bg-slate-100 rounded-full flex items-center justify-center active:scale-95">
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <h1 className="text-2xl font-bold tracking-tight text-slate-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {selectedGroup.name}
                  </h1>
                  {isAdmin && (
                    <button
                      data-testid="rename-group-btn"
                      onClick={() => { setRenameValue(selectedGroup.name); setIsRenaming(true); }}
                      className="w-8 h-8 text-slate-400 hover:text-slate-700 rounded-full flex items-center justify-center hover:bg-slate-100 transition-colors shrink-0">
                      <Pencil size={15} />
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Members Card */}
            <div className="bg-white rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] mb-5">
              <div className="flex items-center justify-between px-5 pt-5 pb-4">
                <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {t('groups.members')} · {t('limits.members_count').replace('{{used}}', selectedGroup.memberDetails?.length || 0).replace('{{max}}', 10)}
                </h3>
                {isAdmin && (
                  <button data-testid="invite-link-button" onClick={() => generateInviteLink(selectedGroup)}
                    className="flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors">
                    <Share2 size={14} />{t('groups.invite')}
                  </button>
                )}
              </div>

              <div className="divide-y divide-slate-50">
                {(selectedGroup.memberDetails || []).map((member) => {
                  const isSelf = member.id === user?.id;
                  const isCreator = member.id === selectedGroup.createdBy;
                  return (
                    <div key={member.id} className="flex items-center gap-3 px-5 py-3" data-testid="member-row">
                      <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-sm font-bold text-slate-600 shrink-0">
                        {member.name?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-semibold text-slate-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {member.name || member.email}
                          </span>
                          {isSelf && <span className="text-xs text-slate-400">{t('groups.you')}</span>}
                        </div>
                        {isCreator && (
                          <div className="flex items-center gap-1 mt-0.5">
                            <Crown size={11} className="text-amber-500" />
                            <span className="text-xs text-amber-600 font-medium">{t('groups.admin_badge')}</span>
                          </div>
                        )}
                      </div>
                      {/* Admin: remove any non-self member */}
                      {isAdmin && !isSelf && (
                        <button
                          data-testid={`remove-member-${member.id}`}
                          onClick={() => setMemberToRemove(member)}
                          className="w-8 h-8 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full flex items-center justify-center transition-colors">
                          <UserMinus size={15} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Shared Reminders */}
            <h3 className="text-base font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t('groups.shared')}
            </h3>
            {!Array.isArray(selectedGroup.reminders) || selectedGroup.reminders.length === 0 ? (
              <div className="bg-white p-8 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] text-center mb-5">
                <p className="text-slate-500 text-sm" style={{ fontFamily: 'Figtree, sans-serif' }}>{t('groups.no_shared')}</p>
              </div>
            ) : (
              <div className="space-y-3 mb-5">
                {selectedGroup.reminders.map((reminder) => {
                  const isHijri = reminder.calendarType === 'hijri';
                  return (
                    <div key={reminder.id} onClick={() => navigate(`/reminder/${reminder.id}`)}
                      className="bg-white p-4 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] flex items-center gap-4 cursor-pointer active:scale-[0.98] transition-all">
                      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${isHijri ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                        {isHijri ? <Moon size={20} /> : <CalendarDays size={20} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-slate-900 font-semibold truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{reminder.title}</h4>
                        <p className="text-slate-500 text-sm">{new Date(reminder.date).toLocaleDateString()}</p>
                        {reminder.createdByName && (
                          <p className="text-xs text-slate-400 mt-0.5">
                            {reminder.createdBy === selectedGroup?.createdBy ? `${reminder.createdByName} · Admin` : reminder.createdByName}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Danger Zone */}
            <div className="space-y-3 mt-2">
              {!isAdmin && (
                <button
                  data-testid="leave-group-btn"
                  onClick={() => setShowLeaveConfirm(true)}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border border-red-200 text-red-600 font-semibold bg-white hover:bg-red-50 active:scale-[0.98] transition-all">
                  <LogOut size={16} />
                  {t('groups.leave')}
                </button>
              )}
              {isAdmin && (
                <button
                  data-testid="delete-group-btn"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border border-red-200 text-red-600 font-semibold bg-white hover:bg-red-50 active:scale-[0.98] transition-all">
                  <Trash2 size={16} />
                  {t('groups.delete_group')}
                </button>
              )}
            </div>
          </motion.div>
        </div>

        {/* Invite Modal (detail view) */}
        <AnimatePresence>
          {showInviteModal && (
            <div className="fixed inset-0 bg-black/50 flex items-end justify-center" style={{ zIndex: 10000 }}>
              <motion.div initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 100, opacity: 0 }}
                className="bg-white w-full max-w-md rounded-t-3xl p-6 pb-8">
                <h2 className="text-xl font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('groups.invite_title')}</h2>
                <p className="text-sm text-slate-500 mb-4" style={{ fontFamily: 'Figtree, sans-serif' }}>{t('groups.invite_desc')}</p>

                {/* Group Code */}
                {inviteCode && (
                  <div className="mb-4">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-1.5">{t('groups.code_label')}</p>
                    <div className="flex items-center gap-3 bg-slate-50 rounded-2xl px-4 py-3">
                      <span className="text-2xl font-bold text-slate-900 tracking-[0.2em] flex-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{inviteCode}</span>
                      <button onClick={copyInviteCode} data-testid="copy-group-code"
                        className="flex items-center gap-1.5 text-sm font-semibold text-blue-600 shrink-0">
                        <Copy size={14} />
                        {copiedCode ? t('groups.copied') : t('groups.copy_code')}
                      </button>
                    </div>
                    <p className="text-xs text-slate-400 mt-1.5 px-1">{t('groups.code_hint')}</p>
                  </div>
                )}

                {/* Invite Link */}
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-1.5">{t('groups.invite_link_label')}</p>
                <div className="bg-slate-50 p-4 rounded-xl mb-4 break-all text-sm text-slate-700 font-mono">{inviteLink}</div>
                <div className="flex gap-3">
                  <button onClick={() => setShowInviteModal(false)} className="flex-1 bg-slate-100 text-slate-900 py-3 rounded-full font-semibold">{t('groups.close')}</button>
                  <button onClick={copyInviteLink} data-testid="copy-invite-link" className="flex-1 bg-slate-900 text-white py-3 rounded-full font-semibold">{copiedLink ? t('groups.copied') : t('groups.copy')}</button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Remove Member Confirm */}
        <AnimatePresence>
          {memberToRemove && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6" style={{ zIndex: 10000 }}>
              <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white w-full max-w-sm rounded-3xl p-6">
                <div className="text-center mb-6">
                  <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <UserMinus size={28} className="text-red-600" />
                  </div>
                  <h2 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('groups.remove_member')}</h2>
                  <p className="text-slate-500 text-sm">{memberToRemove.name}?</p>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setMemberToRemove(null)} className="flex-1 bg-slate-100 text-slate-900 py-3 rounded-full font-semibold">{t('common.cancel')}</button>
                  <button onClick={handleRemoveMember} disabled={removing} data-testid="confirm-remove-member"
                    className="flex-1 bg-red-600 text-white py-3 rounded-full font-semibold disabled:opacity-50">{removing ? '...' : t('groups.remove_member')}</button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Leave Confirm */}
        <AnimatePresence>
          {showLeaveConfirm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6" style={{ zIndex: 10000 }}>
              <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white w-full max-w-sm rounded-3xl p-6">
                <div className="text-center mb-6">
                  <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <LogOut size={28} className="text-orange-600" />
                  </div>
                  <h2 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('groups.leave')}</h2>
                  <p className="text-slate-500 text-sm">{t('groups.leave_confirm')}</p>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setShowLeaveConfirm(false)} className="flex-1 bg-slate-100 text-slate-900 py-3 rounded-full font-semibold">{t('common.cancel')}</button>
                  <button onClick={handleLeave} disabled={leaving} data-testid="confirm-leave-group"
                    className="flex-1 bg-orange-500 text-white py-3 rounded-full font-semibold disabled:opacity-50">{leaving ? '...' : t('groups.leave')}</button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Delete Confirm */}
        <AnimatePresence>
          {showDeleteConfirm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6" style={{ zIndex: 10000 }}>
              <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white w-full max-w-sm rounded-3xl p-6">
                <div className="text-center mb-6">
                  <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Trash2 size={28} className="text-red-600" />
                  </div>
                  <h2 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('groups.delete_group')}</h2>
                  <p className="text-slate-500 text-sm">{t('groups.delete_group_confirm')}</p>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setShowDeleteConfirm(false)} className="flex-1 bg-slate-100 text-slate-900 py-3 rounded-full font-semibold">{t('common.cancel')}</button>
                  <button onClick={handleDeleteGroup} disabled={deleting} data-testid="confirm-delete-group"
                    className="flex-1 bg-red-600 text-white py-3 rounded-full font-semibold disabled:opacity-50">{deleting ? '...' : t('groups.delete_group')}</button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        <BottomNav />
      </div>
    );
  }

  // ── GROUPS LIST VIEW ─────────────────────────────────────────
  return (
    <div className="w-full max-w-md mx-auto min-h-screen bg-[#F7F9FB] relative flex flex-col">
      <div className="p-6 pb-24 flex-1">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('groups.title')}</h1>
            <div className="flex items-center gap-2">
              <button data-testid="join-by-code-button" onClick={() => { setShowJoinCodeModal(true); setJoinError(''); setJoinCodeInput(''); }}
                className="h-12 px-4 bg-white border border-slate-200 text-slate-700 rounded-full flex items-center gap-2 font-semibold text-sm active:scale-95 transition-transform shadow-sm">
                {t('groups.join_by_code')}
              </button>
              <button data-testid="create-group-button" onClick={() => setShowCreateModal(true)}
                className="w-12 h-12 bg-slate-900 text-white rounded-full flex items-center justify-center active:scale-95 transition-transform shadow-lg">
                <Plus size={24} />
              </button>
            </div>
          </div>

          {groups.length === 0 ? (
            <div className="bg-white p-8 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] text-center">
              <Users className="mx-auto mb-3 text-slate-300" size={48} strokeWidth={1.5} />
              <p className="text-slate-500 mb-2 font-medium" style={{ fontFamily: 'Figtree, sans-serif' }}>{t('groups.no_groups')}</p>
              <p className="text-sm text-slate-400" style={{ fontFamily: 'Figtree, sans-serif' }}>{t('groups.no_groups_desc')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {groups.map((group) => (
                <div key={group.id} onClick={() => navigate(`/groups/${group.id}`)} data-testid="group-list-item"
                  className="bg-white p-5 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] border border-slate-50 flex flex-col gap-3 active:scale-[0.98] transition-all cursor-pointer">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{group.name}</h3>
                      {group.createdBy === user?.id && <Crown size={14} className="text-amber-500" />}
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); generateInviteLink(group); }}
                      className="text-blue-600 p-2 rounded-full hover:bg-blue-50 transition-colors">
                      <Share2 size={18} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex -space-x-2">
                      {(group.memberDetails || []).slice(0, 5).map((member, idx) => (
                        <div key={idx} className="w-8 h-8 rounded-full border-2 border-white bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600">
                          {member.name?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                      ))}
                    </div>
                    <span className="text-sm text-slate-500">
                      {group.memberDetails?.length || group.members?.length || 0} {t('home.members')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Create Group Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-end justify-center" style={{ zIndex: 10000 }}>
            <motion.div initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 100, opacity: 0 }}
              className="bg-white w-full max-w-md rounded-t-3xl p-6 pb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('groups.create')}</h2>
              <form onSubmit={handleCreateGroup}>
                <input type="text" required value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder={t('groups.name_placeholder')}
                  data-testid="group-name-input"
                  autoFocus
                  className="w-full bg-slate-50 border border-transparent focus:border-slate-300 focus:bg-white rounded-xl px-4 py-3 text-slate-900 transition-colors placeholder:text-slate-400 mb-4" />
                <div className="flex gap-3">
                  <button type="button" onClick={() => { setShowCreateModal(false); setNewGroupName(''); }}
                    className="flex-1 bg-slate-100 text-slate-900 py-3 rounded-full font-semibold">{t('common.cancel')}</button>
                  <button type="submit" disabled={creating || !newGroupName.trim()} data-testid="create-group-submit"
                    className="flex-1 bg-slate-900 text-white py-3 rounded-full font-semibold disabled:opacity-40">
                    {creating ? t('groups.creating') : t('groups.create')}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Invite Modal (list view) */}
      <AnimatePresence>
        {showInviteModal && (
          <div className="fixed inset-0 bg-black/50 flex items-end justify-center" style={{ zIndex: 10000 }}>
            <motion.div initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 100, opacity: 0 }}
              className="bg-white w-full max-w-md rounded-t-3xl p-6 pb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('groups.invite_title')}</h2>
              <p className="text-sm text-slate-500 mb-4">{t('groups.invite_desc')}</p>

              {/* Group Code */}
              {inviteCode && (
                <div className="mb-4">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-1.5">{t('groups.code_label')}</p>
                  <div className="flex items-center gap-3 bg-slate-50 rounded-2xl px-4 py-3">
                    <span className="text-2xl font-bold text-slate-900 tracking-[0.2em] flex-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{inviteCode}</span>
                    <button onClick={copyInviteCode} data-testid="copy-group-code-list"
                      className="flex items-center gap-1.5 text-sm font-semibold text-blue-600 shrink-0">
                      <Copy size={14} />
                      {copiedCode ? t('groups.copied') : t('groups.copy_code')}
                    </button>
                  </div>
                  <p className="text-xs text-slate-400 mt-1.5 px-1">{t('groups.code_hint')}</p>
                </div>
              )}

              {/* Invite Link */}
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-1.5">{t('groups.invite_link_label')}</p>
              <div className="bg-slate-50 p-4 rounded-xl mb-4 break-all text-sm text-slate-700 font-mono">{inviteLink}</div>
              <div className="flex gap-3">
                <button onClick={() => setShowInviteModal(false)} className="flex-1 bg-slate-100 text-slate-900 py-3 rounded-full font-semibold">{t('groups.close')}</button>
                <button onClick={copyInviteLink} className="flex-1 bg-slate-900 text-white py-3 rounded-full font-semibold">{copiedLink ? t('groups.copied') : t('groups.copy')}</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Join by Code Modal */}
      <AnimatePresence>
        {showJoinCodeModal && (
          <div className="fixed inset-0 bg-black/50 flex items-end justify-center" style={{ zIndex: 10000 }}>
            <motion.div initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 100, opacity: 0 }}
              className="bg-white w-full max-w-md rounded-t-3xl p-6 pb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('groups.join_by_code')}</h2>
                <button onClick={() => setShowJoinCodeModal(false)} className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                  <X size={16} />
                </button>
              </div>
              <p className="text-sm text-slate-500 mb-4">{t('groups.enter_code_desc')}</p>
              <form onSubmit={handleJoinByCode}>
                <input
                  type="text"
                  autoFocus
                  data-testid="join-code-input"
                  value={joinCodeInput}
                  onChange={(e) => { setJoinCodeInput(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6)); setJoinError(''); }}
                  placeholder={t('groups.code_placeholder')}
                  maxLength={6}
                  className="w-full bg-slate-50 border border-transparent focus:border-slate-300 focus:bg-white rounded-2xl px-4 py-4 text-center text-3xl font-bold tracking-[0.3em] text-slate-900 outline-none transition-colors placeholder:text-slate-300 placeholder:text-xl mb-3"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                />
                {joinError && <p className="text-red-500 text-sm text-center mb-3">{joinError}</p>}
                <div className="flex gap-3">
                  <button type="button" onClick={() => setShowJoinCodeModal(false)} className="flex-1 bg-slate-100 text-slate-900 py-3 rounded-full font-semibold">{t('common.cancel')}</button>
                  <button type="submit" disabled={joining || joinCodeInput.length !== 6} data-testid="join-code-submit"
                    className="flex-1 bg-slate-900 text-white py-3 rounded-full font-semibold disabled:opacity-40">
                    {joining ? t('groups.joining') : t('groups.join_btn')}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <BottomNav />
    </div>
  );
};

export default GroupsPage;
